/**
 * @file     OwnerListingCard.tsx
 * @readme   ./CLAUDE.md
 * @purpose  The owner listing variant of the bare card and the row (design 3a,
 *           RentalCard "owner"): boxed on the surface with a primary rim and
 *           halo, the photo radius dropped to 12 so the corners stay
 *           concentric, the solid "Listed by Ray Rasouli" tag in the Compare
 *           slot (no Compare: owner listings never enter the compare tray),
 *           rent per month, availability, rental icons, the licensee line and
 *           Add to list. Display comes from toDisplay() like every card.
 */
'use client';

import '@/core/i18n/deferred/rentalsSearch';
import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useHydratedNow } from '@/hooks/useHydratedNow';
import { heroGradient } from '@/design-system/tokens';
import { layoutTokens } from '@/design-system/tokens/layout';
import { signInRoute } from '@/core/constants/routes';
import { toDisplay, type PropertyCardProps } from '../../services/cardAdapters';
import {
  AddToListButton,
  CardIconActions,
  PhotoCountPill,
  PhotoScrim,
  WatchedSnackbar,
  stretchedLinkSx,
} from './cardChrome';
import {
  AvailabilityBadge,
  FairRentChip,
  LicenseeAttribution,
  OwnerListedTag,
  RentPrice,
  RentalFeatureIcons,
} from './rentalCardParts';
import { useOwnerListingSaved } from './useOwnerListingSaves';

export type OwnerCardProps = Extract<PropertyCardProps, { source: 'owner' }> & {
  /** Fires when the card opens the listing (the band's owner_band_click). */
  onOpen?: () => void;
  imageSizes?: string;
};

const noop = () => {};

function useOwnerCard(props: OwnerCardProps) {
  const { theme } = useAppTheme();
  const { t } = useTranslation();
  const { trackCtaClick } = useAnalytics();
  const now = useHydratedNow();
  const display = toDisplay(props, { now });
  const { saved, toggle } = useOwnerListingSaved(props.listing.id);
  const locationTag = props.locationTag ?? 'owner_listing_card';
  // Signed out, a save waits on this device: the toast offers sign-in.
  const [signInHref, setSignInHref] = useState<string | null>(null);
  const onToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const where = toggle();
    if (where === 'signed_out' && !saved) setSignInHref(signInRoute(`${window.location.pathname}${window.location.search}`));
    trackCtaClick(saved ? 'Remove Owner Listing' : 'Save Owner Listing', locationTag, display.href, {
      listing_type: display.listingType,
      listing_source: 'owner',
      listing_id: props.listing.id,
    });
  };
  const onLinkClick = () => {
    props.onOpen?.();
    trackCtaClick('View Property', locationTag, display.href, {
      listing_type: display.listingType,
      listing_source: 'owner',
      listing_id: props.listing.id,
    });
  };
  const specs = [
    display.beds != null ? `${display.beds} ${t('properties.card.bd', { defaultValue: 'bd' })}` : null,
    display.baths != null ? `${display.baths} ${t('properties.card.ba', { defaultValue: 'ba' })}` : null,
    display.sqft != null ? `${display.sqft.toLocaleString('en-CA')} ${t('properties.card.sqft', { defaultValue: 'sqft' })}` : null,
  ].filter((s): s is string => s != null);
  const placeLine = [display.subtitle, display.propertyType].filter(Boolean).join(' · ');
  // Owner rim: 1px primary line, surface fill and a 3px primary halo at 8%.
  const boxSx = {
    position: 'relative',
    isolation: 'isolate',
    border: `1px solid ${alpha(theme.rawSemanticColors.interactivePrimary, 0.55)}`,
    borderRadius: `${layoutTokens.radius.lg}px`,
    backgroundColor: theme.semanticColors.surfaceCard,
    boxShadow: `0 0 0 3px ${alpha(theme.rawSemanticColors.interactivePrimary, 0.08)}`,
  } as const;
  const snackbar = (
    <WatchedSnackbar open={signInHref != null} onClose={() => setSignInHref(null)} signInHref={signInHref ?? undefined} />
  );
  return { theme, now, display, saved, onToggle, onLinkClick, specs, placeLine, boxSx, snackbar };
}

function PhotoFallbackGlyph() {
  return (
    <Box aria-hidden sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
      <svg viewBox="0 0 48 48" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1.4">
        <rect x="4" y="16" width="40" height="28" rx="2" />
        <path d="M4 22L24 8L44 22" />
        <rect x="18" y="30" width="12" height="14" />
      </svg>
    </Box>
  );
}

/** Grid card (bare anatomy, boxed). */
export function OwnerListingCard(props: OwnerCardProps) {
  const { theme, now, display, saved, onToggle, onLinkClick, specs, placeLine, boxSx, snackbar } = useOwnerCard(props);
  const photoRadius = layoutTokens.radius.sm;
  const hasPhotoCount = (display.photosCount ?? 0) > 1;
  return (
    <>
    <Box
      component="article"
      data-presentation="bare"
      data-source="owner"
      sx={{
        ...boxSx,
        containerType: 'inline-size',
        containerName: 'listing-card',
        height: '100%',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        p: '8px 8px 12px',
      }}
    >
      <Box
        component={Link}
        href={display.href}
        prefetch={false}
        target={props.openInSameTab ? undefined : '_blank'}
        rel={props.openInSameTab ? undefined : 'noopener noreferrer'}
        sx={stretchedLinkSx(theme, layoutTokens.radius.lg)}
        onClick={onLinkClick}
      >
        <Box
          data-card-photo
          sx={{
            position: 'relative',
            zIndex: 2,
            aspectRatio: '4 / 3',
            background: heroGradient.cardOverlay(alpha, theme.rawColors.primaryMuted, theme.rawColors.secondary),
            borderRadius: `${photoRadius}px`,
            overflow: 'hidden',
            isolation: 'isolate',
          }}
        >
          {display.primaryImageSrc ? (
            <Image
              src={display.primaryImageSrc}
              alt={display.title}
              fill
              quality={75}
              sizes={props.imageSizes ?? '(max-width: 899px) 50vw, 320px'}
              unoptimized={display.primaryImageSrc.includes('?')}
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <PhotoFallbackGlyph />
          )}
          {display.primaryImageSrc ? <PhotoScrim /> : null}
          <AvailabilityBadge availableOn={display.availableOn} now={now} hasPhotoCount={hasPhotoCount} />
          {hasPhotoCount ? (
            <Box sx={{ display: { xs: 'none', sm: 'contents' } }}>
              <PhotoCountPill count={display.photosCount ?? 0} />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ pt: 2, px: 0.5, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
          <RentPrice amount={display.price} />
          {specs.length > 0 ? (
            <Box component="p" data-testid="card-specs" sx={{ m: 0, fontSize: 14, lineHeight: 1.45, color: theme.semanticColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
              {specs.join(' · ')}
            </Box>
          ) : null}
          <RentalFeatureIcons features={display.rentalFeatures} />
          <Box
            component="p"
            title={display.title}
            sx={{ m: 0, fontSize: 14, fontWeight: 500, lineHeight: 1.35, color: theme.semanticColors.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {display.title}
          </Box>
          {placeLine ? (
            <Box component="p" data-testid="card-place" sx={{ m: 0, fontSize: 12.5, lineHeight: 1.4, color: theme.semanticColors.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {placeLine}
            </Box>
          ) : null}
        </Box>
      </Box>

      {props.fairRent ? (
        <Box sx={{ mt: 0.5, px: 0.5, display: 'flex' }}>
          <FairRentChip band={props.fairRent} />
        </Box>
      ) : null}

      {/* Overlay layer over the photo box (inside the 8px padding), so the tag
          and Save sit at the photo's own 10px inset. Siblings of the link. */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          right: 8,
          aspectRatio: '4 / 3',
          zIndex: 3,
          pointerEvents: 'none',
          '& [data-card-actions]': { pointerEvents: 'auto' },
        }}
      >
        <OwnerListedTag />
        {!props.hideLikeButton ? (
          <CardIconActions watched={saved} onWatch={onToggle} inCompare={false} canCompare={false} onCompare={noop} showCompare={false} />
        ) : null}
      </Box>

      <Box sx={{ px: 0.5, mt: 'auto' }}>
        <LicenseeAttribution />
      </Box>

      {!props.hideLikeButton ? (
        <Box sx={{ px: 0.5 }}>
          <AddToListButton onList={saved} onToggle={onToggle} />
        </Box>
      ) : null}
    </Box>
    {snackbar}
    </>
  );
}

/** Row (ListingRow anatomy, boxed): 168px thumbnail at radius 12, the "Listed by Ray" tag on it. */
export function OwnerListingRow(props: OwnerCardProps & { actions?: React.ReactNode }) {
  const { theme, now, display, saved, onToggle, onLinkClick, specs, placeLine, boxSx, snackbar } = useOwnerCard(props);
  return (
    <>
    <Box
      component="article"
      data-listing-row
      data-source="owner"
      sx={{
        ...boxSx,
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 120px) minmax(0, 1fr)', sm: '168px minmax(0, 1fr) auto' },
        columnGap: { xs: 3, sm: 4 },
        rowGap: 2,
        alignItems: 'start',
        p: '8px',
      }}
    >
      <Box
        component={Link}
        href={display.href}
        prefetch={false}
        target={props.openInSameTab ? undefined : '_blank'}
        rel={props.openInSameTab ? undefined : 'noopener noreferrer'}
        aria-label={display.title}
        onClick={onLinkClick}
        sx={{ ...stretchedLinkSx(theme, layoutTokens.radius.lg), position: 'absolute', inset: 0 }}
      />
      <Box
        data-card-photo
        sx={{
          position: 'relative',
          aspectRatio: '4 / 3',
          borderRadius: `${layoutTokens.radius.sm}px`,
          overflow: 'hidden',
          background: heroGradient.cardOverlay(alpha, theme.rawColors.primaryMuted, theme.rawColors.secondary),
        }}
      >
        {display.primaryImageSrc ? (
          <Image src={display.primaryImageSrc} alt="" fill sizes="168px" quality={75} unoptimized={display.primaryImageSrc.includes('?')} style={{ objectFit: 'cover' }} />
        ) : (
          <PhotoFallbackGlyph />
        )}
        {display.primaryImageSrc ? <PhotoScrim /> : null}
        <OwnerListedTag size="row" />
        <AvailabilityBadge availableOn={display.availableOn} now={now} hasPhotoCount={false} size="row" />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, py: 0.5 }}>
        <RentPrice amount={display.price} size="row" />
        {specs.length > 0 ? (
          <Box component="p" data-testid="card-specs" sx={{ m: 0, fontSize: 13.5, lineHeight: 1.45, color: theme.semanticColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
            {specs.join(' · ')}
          </Box>
        ) : null}
        <RentalFeatureIcons features={display.rentalFeatures} />
        <Box component="p" sx={{ m: 0, fontSize: 14, fontWeight: 500, lineHeight: 1.35, color: theme.semanticColors.textPrimary, overflowWrap: 'anywhere' }}>
          {display.title}
        </Box>
        {placeLine ? (
          <Box component="p" data-testid="card-place" sx={{ m: 0, fontSize: 12.5, lineHeight: 1.4, color: theme.semanticColors.textMuted }}>
            {placeLine}
          </Box>
        ) : null}
        {props.fairRent ? <FairRentChip band={props.fairRent} /> : null}
        <Box sx={{ pt: 1 }}>
          <LicenseeAttribution compact />
        </Box>
      </Box>

      {props.actions ? (
        <Box sx={{ position: 'relative', zIndex: 2, gridColumn: { xs: '1 / -1', sm: 'auto' }, alignSelf: { sm: 'center' } }}>{props.actions}</Box>
      ) : !props.hideLikeButton ? (
        <Box sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' }, alignSelf: { sm: 'center' }, justifySelf: { xs: 'stretch', sm: 'end' }, '& .MuiButton-root': { width: { xs: '100%', sm: 'auto' } } }}>
          <AddToListButton onList={saved} onToggle={onToggle} size="row" />
        </Box>
      ) : null}
    </Box>
    {snackbar}
    </>
  );
}
